import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, Banknote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, EXPENSE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const empty = { name: "", type: "bank", opening_balance: 0 };

export default function Accounts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.get("/accounts").then((r) => r.data),
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Indique um nome");
    try {
      const payload = { ...form, opening_balance: parseFloat(String(form.opening_balance).replace(",", ".")) || 0 };
      if (form.id) await api.put(`/accounts/${form.id}`, payload);
      else await api.post("/accounts", payload);
      toast.success("Conta guardada");
      setOpen(false);
      setForm(empty);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Apagar conta e respetivas transações?")) return;
    await api.delete(`/accounts/${id}`);
    toast.success("Conta apagada");
    qc.invalidateQueries();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Contas</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-account-btn"><Plus size={16} className="mr-1" /> Nova conta</Button>
          </DialogTrigger>
          <DialogContent data-testid="account-dialog">
            <DialogHeader><DialogTitle>{form.id ? "Editar conta" : "Nova conta"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Millennium, Dinheiro" data-testid="account-name" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Conta bancária</SelectItem>
                    <SelectItem value="cash">Dinheiro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Saldo inicial (€)</Label>
                <Input value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="account-opening" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="account-save">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="accounts-list">
        {accounts.map((a) => (
          <div key={a.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between" data-testid={`account-item-${a.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                {a.type === "cash" ? <Banknote size={18} className="text-primary" /> : <Wallet size={18} className="text-primary" />}
              </div>
              <div>
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.type === "cash" ? "Dinheiro" : "Conta bancária"}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="font-heading text-lg font-bold" style={{ color: a.balance >= 0 ? "#4A5D4E" : EXPENSE }}>
                {fmtEUR(a.balance)}
              </div>
              <button onClick={() => { setForm({ id: a.id, name: a.name, type: a.type, opening_balance: a.opening_balance }); setOpen(true); }} className="text-muted-foreground hover:text-primary p-1" data-testid={`account-edit-${a.id}`}>
                <Pencil size={16} />
              </button>
              <button onClick={() => del(a.id)} className="text-muted-foreground hover:text-[#C75D42] p-1" data-testid={`account-delete-${a.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
